import { buildMongoQueryMatcher, mongoQueryMatcher } from '@casl/ability'
import { DateTemplateCases } from '@/@vxapp/@Enums/PHP/core/Enums/DateTemplate'

import type { ConditionFields, RecordPredicate } from '@/@vxapp/Auth/permissionConditions'

export type { ConditionFields, RecordPredicate } from '@/@vxapp/Auth/permissionConditions'
export const conditionOperators = [
  '$eq',
  '$ne',
  '$in',
  '$nin',
  '$gt',
  '$gte',
  '$lt',
  '$lte',
] as const
const logicalOperators = ['$and', '$or']
const comparisonOperators = new Set(['$gt', '$gte', '$lt', '$lte'])
const datePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/

const recordDatePattern =
  /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d{1,6}))?(Z|[+-]\d{2}:\d{2})$/

function object(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}
function canonicalDate(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    datePattern.test(value) &&
    Number.isFinite(Date.parse(value)) &&
    new Date(value).toISOString() === value.replace('Z', '.000Z')
  )
}

/** Checks editable input; projected snapshots must already have resolved date templates. */
export function validateRecordConditions(
  predicate: unknown,
  fields: ConditionFields | null,
  allowTemplates = true
): string | null {
  if (predicate === null) return null
  if (!object(predicate)) return 'Conditions must be a JSON object or null.'
  if (new TextEncoder().encode(JSON.stringify(predicate)).length > 8192)
    return 'Conditions cannot exceed 8 KiB.'
  if (!Object.keys(predicate).length) return null
  if (!fields) return 'This operation does not support record conditions.'
  if (
    !object(fields) ||
    Object.values(fields).some(type => !['integer', 'string', 'datetime'].includes(type))
  )
    return 'The operation condition field schema is unavailable.'
  let nodes = 0
  function operand(value: unknown, type: ConditionFields[string]): boolean {
    if (type === 'integer') return typeof value === 'number' && Number.isSafeInteger(value)
    if (type === 'string') return typeof value === 'string'

    return (
      canonicalDate(value) ||
      (allowTemplates &&
        typeof value === 'string' &&
        (DateTemplateCases as readonly string[]).includes(value))
    )
  }
  function visit(value: unknown, depth: number): string | null {
    if (depth > 6) return 'Conditions cannot exceed depth 6.'
    if (!object(value) || !Object.keys(value).length)
      return 'Logical branches must be nonempty objects.'
    for (const [field, query] of Object.entries(value)) {
      if (++nodes > 64) return 'Conditions cannot exceed 64 predicate nodes.'
      if (logicalOperators.includes(field)) {
        if (!Array.isArray(query) || !query.length) return `${field} requires nonempty branches.`
        for (const branch of query) {
          const error = visit(branch, depth + 1)
          if (error) return error
        }
        continue
      }
      if (!Object.hasOwn(fields!, field)) return `Unsupported condition field: ${field}.`
      const type = fields![field]

      const entries: Array<[string, unknown]> = object(query)
        ? Object.entries(query)
        : [['$eq', query]]

      if (!entries.length) return `${field} requires a predicate.`
      for (const [operator, argument] of entries) {
        if (!(conditionOperators as readonly string[]).includes(operator))
          return `Unsupported operator: ${operator}.`
        if (comparisonOperators.has(operator) && type === 'string')
          return `${operator} requires an integer or datetime field.`
        if (operator === '$in' || operator === '$nin') {
          if (!Array.isArray(argument) || !argument.length || argument.length > 64)
            return `${operator} requires 1–64 values.`
          if (!argument.every(item => operand(item, type)))
            return `${field} requires ${type} values.`
        } else if (!operand(argument, type)) return `${field} requires a canonical ${type} value.`
      }
    }

    return null
  }

  return visit(predicate, 1)
}

function recordDate(value: unknown): number | null {
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() / 1000 : null
  if (typeof value !== 'string') return null
  const parts = recordDatePattern.exec(value)
  if (!parts) return null
  const dateParts = parts[1].match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/)!
  const [, year, month, day, hour, minute, second] = dateParts.map(Number)
  const calendar = new Date(Date.UTC(year, month - 1, day))
  if (
    calendar.getUTCFullYear() !== year ||
    calendar.getUTCMonth() !== month - 1 ||
    calendar.getUTCDate() !== day ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  )
    return null
  const seconds = Date.parse(`${parts[1]}${parts[3]}`) / 1000

  return Number.isFinite(seconds) ? seconds + Number(`0.${parts[2] ?? '0'}`) : null
}
function referencedFields(predicate: RecordPredicate): string[] {
  return Object.entries(predicate).flatMap(([field, value]) =>
    logicalOperators.includes(field)
      ? (value as RecordPredicate[]).flatMap(referencedFields)
      : [field]
  )
}
function normalizePredicateDates(
  predicate: RecordPredicate,
  fields: ConditionFields
): RecordPredicate {
  return Object.fromEntries(
    Object.entries(predicate).map(([field, query]) => {
      if (logicalOperators.includes(field))
        return [
          field,
          (query as RecordPredicate[]).map(branch => normalizePredicateDates(branch, fields)),
        ]
      if (fields[field] !== 'datetime') return [field, query]

      const entries: Array<[string, unknown]> = object(query)
        ? Object.entries(query)
        : [['$eq', query]]

      return [
        field,
        Object.fromEntries(
          entries.map(([operator, value]) => [
            operator,
            Array.isArray(value)
              ? value.map(item => Date.parse(item) / 1000)
              : Date.parse(value as string) / 1000,
          ])
        ),
      ]
    })
  )
}

export function recordConditionEnvelope(predicate: RecordPredicate, fields: ConditionFields) {
  return { __vxRecordPredicate: predicate, __vxConditionFields: fields }
}

/** Shared by the installed Ability, so useAbility/$can/module consumers apply identical predicates. */
export function applicationConditionsMatcher(conditions: Record<string, unknown>) {
  if (!Object.hasOwn(conditions, '__vxRecordPredicate')) return mongoQueryMatcher(conditions)
  const predicate = conditions.__vxRecordPredicate as RecordPredicate
  const fields = conditions.__vxConditionFields as ConditionFields
  if (validateRecordConditions(predicate, fields, false)) return () => false
  if (!Object.keys(predicate).length) return (record: unknown) => object(record)

  // Every referenced fact in this rule is required; unreferenced declared fields are not.
  const matcher = buildMongoQueryMatcher(
    { $and: { type: 'compound' }, $or: { type: 'compound' } },
    {
      or: (condition, record, context) =>
        condition.value.some((branch: Parameters<typeof context.interpret>[0]) =>
          context.interpret(branch, record)
        ),
    }
  )

  const matches = matcher(normalizePredicateDates(predicate, fields))
  const required = [...new Set(referencedFields(predicate))]

  return (record: Record<string, unknown>) => {
    if (!object(record)) return false
    const normalized = { ...record }
    if (!Object.hasOwn(normalized, 'created_at') && Object.hasOwn(normalized, 'createdAt'))
      normalized.created_at = normalized.createdAt
    for (const field of required) {
      if (
        !Object.hasOwn(normalized, field) ||
        normalized[field] === null ||
        normalized[field] === undefined
      )
        return false
      const type = fields[field]
      const value = normalized[field]
      if (type === 'integer' && (typeof value !== 'number' || !Number.isSafeInteger(value)))
        return false
      if (type === 'string' && typeof value !== 'string') return false
      if (type === 'datetime') {
        const date = recordDate(value)
        if (date === null) return false
        normalized[field] = date
      }
    }

    return matches(normalized)
  }
}
